import { useState, useEffect, useRef } from 'react'
import { MessageDock, type Character } from '~/client/components/ui/shadcn-io/message-dock'
import { adminActions, type AdminAction } from '~/client/admin/actions'
import * as Sentry from '@sentry/react'

// Convert admin actions to characters format for the MessageDock
const convertActionsToCharacters = (actions: AdminAction[]): Character[] => {
  // Add decorative elements at start and end
  const characters: Character[] = [{ emoji: '🛡️', name: 'Admin', online: false }]

  // Add all admin actions as characters
  actions.forEach((action) => {
    characters.push({
      id: action.id,
      emoji: action.emoji,
      name: action.name,
      online: true, // Show all actions as available
      backgroundColor: action.backgroundColor,
      gradientColors: action.gradientColors,
    })
  })

  // Add menu button placeholder at the end
  characters.push({
    emoji: '⚙️',
    name: 'Settings',
    online: false,
  })

  return characters
}

export default function QuickAdminActionsDock() {
  const [isVisible, setIsVisible] = useState(false)
  const [isHoveringDock, setIsHoveringDock] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const dockRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const windowHeight = window.innerHeight
      const mouseY = e.clientY
      const threshold = 10 // Show dock when mouse is within 10px from bottom

      // Check if mouse is over the dock element
      const isOverDock = dockRef.current?.contains(e.target as Node)

      if (windowHeight - mouseY <= threshold || isOverDock) {
        setIsVisible(true)
      } else if (!isHoveringDock && !isLoading && !isExpanded) {
        setIsVisible(false)
      }
    }

    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isHoveringDock, isLoading, isExpanded])

  const characters = convertActionsToCharacters(adminActions)

  const handleMessageSend = async (message: string, character: Character, index: number) => {
    // Find the corresponding admin action by character ID
    const action = adminActions.find((a) => a.id === character.id)

    if (action && action.type === 'textInput') {
      setIsLoading(true)
      setLoadingMessage(action.loadingMessage?.(message) || `Executing ${action.name}...`)
      try {
        await action.onAction(message)
      } catch (error) {
        console.error('Error executing action:', error)
        // Report error to Sentry
        Sentry.captureException(error)
        // Re-throw the error so it can be caught by error boundaries if needed
        throw error
      } finally {
        setIsLoading(false)
        setLoadingMessage('')
      }
    } else {
      //console.log('Message sent:', { message, character: character.name, index })
    }
  }

  const handleCharacterSelect = async (character: Character) => {
    const action = adminActions.find((a) => a.id === character.id)
    //console.log('Action selected:', action?.name || character.name)

    // Handle button-type actions immediately when selected
    if (action && action.type === 'button') {
      setIsLoading(true)
      setLoadingMessage(action.loadingMessage?.() || `Executing ${action.name}...`)
      try {
        await action.onAction()
      } catch (error) {
        console.error('Error executing button action:', error)
        // Report error to Sentry
        Sentry.captureException(error)
        // Re-throw the error so it can be caught by error boundaries if needed
        throw error
      } finally {
        setIsLoading(false)
        setLoadingMessage('')
      }
    }
  }

  const handleDockToggle = (expanded: boolean) => {
    setIsExpanded(expanded)
    //console.log('Dock expanded:', expanded)
  }

  const getPlaceholder = (characterName: string) => {
    const character = characters.find((c) => c.name === characterName)
    const action = adminActions.find((a) => a.id === character?.id)
    return action?.placeholder || `Enter ${characterName}...`
  }

  return (
    <div
      ref={dockRef}
      onMouseEnter={() => setIsHoveringDock(true)}
      onMouseLeave={() => setIsHoveringDock(false)}
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-transform duration-300 ease-in-out ${isVisible ? 'translate-y-0' : 'translate-y-[200%]'}`}
    >
      <div className="relative">
        <MessageDock
          characters={characters}
          onMessageSend={handleMessageSend}
          onCharacterSelect={handleCharacterSelect}
          onDockToggle={handleDockToggle}
          expandedWidth={400}
          placeholder={getPlaceholder}
          enableAnimations={true}
          closeOnSend={false}
          autoFocus={true}
          className="relative"
        />

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-full border border-border">
            <div className="flex items-center gap-3 px-6">
              {/* Spinner */}
              <svg className="animate-spin h-5 w-5 text-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm font-medium text-foreground">{loadingMessage}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
